import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ActiveAlert = {
  id: string;
  tinyId: string;
  message: string;
  priority: string;
  status: string;
  acknowledged: boolean;
  createdAt: string;
  updatedAt: string;
  owner: string;
  source: string;
  teamNames: string[];
};

type AlertItem = {
  id: string;
  tinyId: string;
  message: string;
  priority: string;
  status: string;
  acknowledged: boolean;
  createdAt: string;
  updatedAt: string;
  owner: string;
  source: string;
  teams: Array<{ id: string; name?: string }>;
  responders: Array<{ type: string; id: string }>;
};

const CACHE_TTL = 5 * 60 * 1000;
let cached: { data: ActiveAlert[]; ts: number } | undefined;

const LOG_PREFIX = '[useActiveAlerts]';

function getUserGroups(ownershipEntityRefs: string[]): string[] {
  return ownershipEntityRefs
    .filter(ref => ref.startsWith('group:'))
    .map(ref => ref.replace(/^group:[^/]*\//, ''));
}

type UseActiveAlertsResult = {
  value: ActiveAlert[] | undefined;
  loading: boolean;
  error: Error | undefined;
  retry: () => void;
};

export const useActiveAlerts = (): UseActiveAlertsResult => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [value, setValue] = useState<ActiveAlert[] | undefined>(
    cached ? cached.data : undefined,
  );
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | undefined>();
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setValue(cached.data);
      setLoading(false);
      setError(undefined);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(undefined);

    try {
      const identity = await identityApi.getBackstageIdentity();
      const groups = getUserGroups(identity.ownershipEntityRefs);
      if (!groups.length) {
        console.debug(`${LOG_PREFIX} No groups found — skipping alerts fetch`);
        if (mountedRef.current && fetchId === fetchIdRef.current) {
          setValue([]);
          setLoading(false);
        }
        return;
      }

      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const teamQuery = groups.map(g => `teams:${g}`).join(' OR ');
      const query = `status:open AND (${teamQuery})`;
      console.debug(`${LOG_PREFIX} Querying alerts: ${query}`);

      const alertsRes = await fetchApi.fetch(
        `${proxyBase}/opsgenie/v2/alerts?query=${encodeURIComponent(query)}&limit=20&sort=createdAt&order=desc`,
      );

      if (!alertsRes.ok) {
        throw new Error(
          `OpsGenie alerts API returned ${alertsRes.status}: ${alertsRes.statusText}`,
        );
      }

      const alertsJson = await alertsRes.json();
      const allAlerts: AlertItem[] = alertsJson.data ?? [];

      const groupSet = new Set(groups.map(g => g.toLowerCase()));
      const alerts: ActiveAlert[] = allAlerts.map(a => ({
        id: a.id,
        tinyId: a.tinyId,
        message: a.message,
        priority: a.priority,
        status: a.status,
        acknowledged: a.acknowledged,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
        owner: a.owner ?? '',
        source: a.source ?? '',
        teamNames: (a.teams ?? [])
          .filter(t => t.name && groupSet.has(t.name.toLowerCase()))
          .map(t => t.name!),
      }));

      console.debug(
        `${LOG_PREFIX} Found ${alerts.length} alert(s) for teams [${groups.join(', ')}]`,
      );

      cached = { data: alerts, ts: Date.now() };
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        setValue(alerts);
        setLoading(false);
      }
    } catch (err) {
      console.error(`${LOG_PREFIX} Failed to fetch alerts:`, err);
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        if (cached) {
          console.debug(`${LOG_PREFIX} Serving stale cache after error`);
          setValue(cached.data);
        }
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    }
  }, [discoveryApi, fetchApi, identityApi]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  return { value, loading, error, retry: load };
};
